import * as Markdoc from "@markdoc/markdoc";
import React from "react";
import { Highlight, themes } from "prism-react-renderer";
import { cn } from "@/lib/utils";

/** Extract raw code string from pre's children (Markdoc may pass string or single-element array). */
function getCodeFromChildren(children: React.ReactNode): string {
  let out: string;
  if (typeof children === "string") out = children;
  else if (Array.isArray(children) && children.length === 1 && typeof children[0] === "string") out = children[0];
  else if (Array.isArray(children)) out = children.map((c) => (typeof c === "string" ? c : "")).join("");
  else out = String(children ?? "");
  return out.trimEnd();
}

/** Prism language alias (e.g. js -> javascript). */
const LANGUAGE_ALIAS: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  sh: "bash",
  shell: "bash",
};

const headingClass: Record<number, string> = {
  1: "mt-10 mb-4 font-serif text-3xl font-semibold tracking-tight text-foreground first:mt-0",
  2: "mt-8 mb-3 font-serif text-2xl font-semibold tracking-tight text-foreground",
  3: "mt-6 mb-2 font-serif text-xl font-semibold text-foreground",
};

function DocHeading({
  level,
  children,
  className,
  ...props
}: { level?: number; children?: React.ReactNode } & React.HTMLAttributes<HTMLHeadingElement>) {
  const lvl = level ?? 1;
  const Comp = `h${lvl}` as "h1" | "h2" | "h3";
  return React.createElement(Comp, {
    ...props,
    className: cn(headingClass[lvl] ?? headingClass[3], className),
  }, children);
}

const docComponents: Record<string, React.ComponentType<any>> = {
  h1: (p: any) => <DocHeading level={1} {...p} />,
  h2: (p: any) => <DocHeading level={2} {...p} />,
  h3: (p: any) => <DocHeading level={3} {...p} />,
  p: (p: any) => <p {...p} className={cn("mb-4 leading-relaxed text-foreground/90", p.className)} />,
  pre: (p: any) => {
    const code = getCodeFromChildren(p.children);
    const rawLang = (p["data-language"] ?? p.language ?? "").trim().toLowerCase();
    const language = rawLang ? LANGUAGE_ALIAS[rawLang] ?? rawLang : "text";
    const preClassName = cn(
      "my-4 overflow-x-auto rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm",
      p.className,
    );
    if (!code && !rawLang) {
      return <pre {...p} className={preClassName} />;
    }
    return (
      <Highlight theme={themes.oneLight} code={code || " "} language={language}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre className={cn(preClassName, className)} style={style}>
            <code>
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </code>
          </pre>
        )}
      </Highlight>
    );
  },
  blockquote: (p: any) => (
    <blockquote
      {...p}
      className={cn("my-6 border-l-2 border-foreground/20 pl-6 italic text-foreground/80", p.className)}
    />
  ),
  a: (p: any) => {
    const href = p.href ?? "#";
    const isExternal = href.startsWith("http");
    return (
      <a
        {...p}
        href={href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className={cn("text-foreground underline underline-offset-4 transition-opacity hover:opacity-80", p.className)}
      />
    );
  },
  ul: (p: any) => <ul {...p} className={cn("my-4 ml-6 list-disc space-y-2 text-foreground/90", p.className)} />,
  ol: (p: any) => <ol {...p} className={cn("my-4 ml-6 list-decimal space-y-2 text-foreground/90", p.className)} />,
  table: (p: any) => (
    <div className="my-6 w-full overflow-x-auto">
      <table {...p} className={cn("w-full border-collapse text-sm text-foreground/90", p.className)} />
    </div>
  ),
  thead: (p: any) => <thead {...p} className={cn("border-b border-border bg-muted/30", p.className)} />,
  tbody: (p: any) => <tbody {...p} className={p.className} />,
  tr: (p: any) => <tr {...p} className={cn("border-b border-border/60", p.className)} />,
  th: (p: any) => <th {...p} className={cn("px-4 py-2 text-left font-medium text-foreground", p.className)} />,
  td: (p: any) => <td {...p} className={cn("px-4 py-2", p.className)} />,
  img: (p: any) => (
    <img
      {...p}
      className={cn("my-4 max-h-[400px] w-full rounded-lg border border-border object-contain", p.className)}
      alt={p.alt ?? ""}
    />
  ),
  hr: (p: any) => <hr {...p} className={cn("my-8 border-0 border-t border-border/60", p.className)} />,
  code: (p: any) => (
    <code
      {...p}
      className={cn("rounded bg-muted/50 px-1.5 py-0.5 font-mono text-sm text-foreground", p.className)}
    />
  ),
};

/** Strip YAML frontmatter (--- ... ---) so Markdoc does not render it as horizontal rules + paragraph. */
function stripFrontmatter(markdown: string): string {
  const match = markdown.match(/^\s*---\s*\n[\s\S]*?\n\s*---\s*(\n|$)/);
  return match ? markdown.slice(match[0].length) : markdown;
}

/** Use our components for lowercase tag names (p, h1, pre, ...). Markdoc's default only uses components for PascalCase. */
function resolveTagName(
  name: string,
  components: Record<string, React.ComponentType<any>> | ((s: string) => React.ComponentType<any>),
): string | React.ComponentType<any> {
  if (typeof name !== "string") return name;
  if (typeof components === "object" && components != null && name in components) return components[name]!;
  return name;
}

export function renderMarkdoc(markdown: string): React.ReactNode {
  const body = stripFrontmatter(markdown);
  const ast = Markdoc.parse(body);
  const content = Markdoc.transform(ast);
  return Markdoc.renderers.react(content, React, {
    components: docComponents,
    resolveTagName,
  });
}
